import { subject } from '@casl/ability';
import { IonInput, IonItem, IonLabel, IonList } from '@ionic/react';
import { Form, Formik } from 'formik';
import { useCallback, useMemo } from 'react';
import * as yup from 'yup';
import { Can } from '../../../api/authorization.provider';
import {
  ScratchpadPlaylist,
  ScratchpadPlaylistItem,
  UpdateScratchpadPlaylistInput,
  useUpdateScratchpadPlaylistMutation,
} from '../../../api/graphql/generated';
import { trackPlaylistUpdated } from '../../../config/analytics';
import i18n from '../../../i18n';
import { useDateFormatter } from '../../../util/hooks';
import { RightAlignedButtons } from '../../button/aligned-buttons';
import { FormSubmitButton } from '../../form/form-submit.button';
import { IonFormField } from '../../form/ion-form.field';
import { SaveStatusToast } from '../../toast/toast.gql';
import { FieldLocked } from '../../ui/field-locked';

interface ScratchpadPlaylistEditProps {
  playlist?: ScratchpadPlaylist;
}

interface ScratchpadPlaylistForm {
  id: string;
  machineId: string;
  name?: string;
  items?: ScratchpadPlaylistItem[];
}

const schema = yup.object().shape({
  name: yup.string().required('*'),
  items: yup.array().required(),
});

const ScratchpadPlaylistEdit = (props: ScratchpadPlaylistEditProps) => {
  const { playlist } = props;

  const [updatePlaylist, { loading, error }] =
    useUpdateScratchpadPlaylistMutation();

  const updatedAt = useDateFormatter(
    playlist?.updatedAt ?? playlist?.createdAt
  );

  const initialValues = useMemo<ScratchpadPlaylistForm | undefined>(() => {
    if (playlist) {
      // stripe out the typename from the items
      const items = playlist.items?.map((i) => {
        const { __typename, ...rest } = i;
        return rest;
      });

      return {
        id: playlist.id,
        machineId: playlist.machineId,
        name: playlist.canRestore ? playlist.name : undefined,
        items,
      };
    }
  }, [playlist]);

  const onSubmit = useCallback(
    (form: ScratchpadPlaylistForm) => {
      const input: UpdateScratchpadPlaylistInput = {
        ...form,
        // this property should default to true when a user is saving a scratchpad
        canRestore: true,
      };

      updatePlaylist({
        variables: {
          input,
        },
      });

      trackPlaylistUpdated(input.canRestore ?? false);
    },
    [updatePlaylist]
  );

  return initialValues ? (
    <>
      <Formik
        initialValues={initialValues}
        onSubmit={onSubmit}
        enableReinitialize={true}
        validationSchema={schema}
      >
        {({ isValid, dirty }) => {
          return (
            <>
              <Form>
                <IonList lines="none">
                  <IonFormField
                    label={i18n.t('common:name')}
                    name="name"
                    inputProps={{
                      type: 'text',
                      autocorrect: 'on',
                      autocapitalize: 'on',
                    }}
                  />
                  <IonItem lines={'full'}>
                    <IonInput
                      aria-label="last-updated-at"
                      labelPlacement="floating"
                      type="text"
                      readonly={true}
                      value={updatedAt}
                    >
                      <div slot="label">
                        <IonLabel color="primary">
                          {i18n.t('common:last_updated')}{' '}
                          <FieldLocked show={true} />
                        </IonLabel>
                      </div>
                    </IonInput>
                  </IonItem>
                </IonList>
                <div style={{ paddingTop: '8px', paddingBottom: '8px' }}>
                  <RightAlignedButtons>
                    <Can
                      I="update"
                      this={subject('scratchpad_playlist', { playlist })}
                    >
                      <FormSubmitButton
                        loading={loading}
                        disabled={!isValid || !dirty}
                        label={
                          initialValues?.name
                            ? i18n.t('common:update')
                            : i18n.t('common:save')
                        }
                        placement="none"
                      />
                    </Can>
                  </RightAlignedButtons>
                </div>
              </Form>
            </>
          );
        }}
      </Formik>
      <SaveStatusToast loading={loading} error={error} />
    </>
  ) : null;
};

export { ScratchpadPlaylistEdit };
